import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import {
  Container,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  Card,
  Tooltip,
  Stack
} from '@mui/material';
import { Search, Refresh } from '@mui/icons-material';
import HeaderHelpPage from '../../../components/HeaderHelpPage';
import CustomDatePicker from '../../../components/form/CustomDatePicker';
import { sendDataWithPost } from '../../../services/services';
import { showSuccessMsg, showErrorMsg } from '../../../utils/toast';
import moment from 'moment';
import { COLORS } from '../../../utils/colors';
import CircularLoader from '../../../components/circularLoader';
import ButtonCustom from '../../../components/ButtonCustom';
import { styles } from '../../../utils/styles';
import { OXYGEN7_ENTITIES, DEFAULT_OXYGEN7_ENTITY } from '../../../utils/constants';
import { StorageUtils } from '../../../utils';
import { APP_USER_SESSION } from '../../../constant/AppConstants';

const MerchantReports = () => {
  const userSession = StorageUtils.getParsedValue(APP_USER_SESSION);
  const userEntity = userSession?.oxygen7_entity || null;
  const availableOxygen7Entities = userEntity 
    ? OXYGEN7_ENTITIES.filter(opt => opt.value === userEntity || opt.sessionEntity === userEntity) 
    : OXYGEN7_ENTITIES;

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [settlementDate, setSettlementDate] = useState(new Date());
  const [selectedMerchantId, setSelectedMerchantId] = useState(null); // null for "all"
  const [selectedBusinessEntityId, setSelectedBusinessEntityId] = useState(null);
  const [selectedOxygen7Entity, setSelectedOxygen7Entity] = useState(availableOxygen7Entities[0]?.value || DEFAULT_OXYGEN7_ENTITY);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  const [allMerchants, setAllMerchants] = useState([]);
  const [allBusinessEntities, setAllBusinessEntities] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [businessEntities, setBusinessEntities] = useState([]);
  const [merchantBusinessEntityMapping, setMerchantBusinessEntityMapping] = useState({});
  const [openDownloadDialog, setOpenDownloadDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  
  const loadReports = useCallback(async () => {
    setLoading(true);
    
    const handleSuccess = (response) => {
      const allReports = [];
      const registeredCountryFromResponse = response.data?.registered_country || selectedOxygen7Entity;
      
      if (response.data?.result) {
        response.data.result.forEach((file, index) => {
          const reportName = file.report_name || 'N/A';
          const reportPath = file.report_path || '';
          
          allReports.push({
            id: index + 1,
            file_name: reportName,
            file_size_formatted: file.file_size || 'N/A',
            upload_date_formatted: file.payout_date ? moment(file.payout_date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
            report_path: reportPath,
            business_id: file.business_id,
            payout_date: file.payout_date,
            registered_country: registeredCountryFromResponse
          });
        });
      }
      // Update merchants from merchant_final_payout array
      if (response.data?.merchant_final_payout && Array.isArray(response.data.merchant_final_payout)) {
        setAllMerchants(response.data.merchant_final_payout);
      } else {
        setAllMerchants([]);
      }
      
      if (response.data?.business_entities && Array.isArray(response.data.business_entities)) {
        setAllBusinessEntities(response.data.business_entities);
      } else {
        setAllBusinessEntities([]);
      }

      const mapping = response.data?.merchant_business_entity_mapping || {};
      setMerchantBusinessEntityMapping(mapping)
      
      setReports(allReports);
      setLoading(false);
    };

    const handleError = (error) => {
      showErrorMsg('Failed to load merchant reports');
      setLoading(false);
      setReports([]);
    };

    const filters = {
      payout_date: moment(settlementDate).format('YYYY-MM-DD'),
      merchant_id: selectedMerchantId, // null for "all", otherwise the merchant_id
      business_entity_id: selectedBusinessEntityId, // null for "all", otherwise the business_entity_id
      registered_country: selectedOxygen7Entity
    };

    sendDataWithPost(
      '/payout/report/merchant_reports/files_uploaded/',
      filters,
      null,
      null,
      handleSuccess,
      handleError
    );
  }, [settlementDate, selectedMerchantId, selectedBusinessEntityId, selectedOxygen7Entity]);

  useEffect(() => {
    setSelectedBusinessEntityId(null);
    setSelectedMerchantId(null);
  }, [selectedOxygen7Entity]);

  useEffect(() => {
    const filteredBusinessEntities = selectedOxygen7Entity
      ? allBusinessEntities.filter((be) => be.registered_country === selectedOxygen7Entity)
      : allBusinessEntities;
    setBusinessEntities(filteredBusinessEntities);

    const merchantsByCountry = selectedOxygen7Entity
      ? allMerchants.filter((m) => m.registered_country === selectedOxygen7Entity)
      : allMerchants;

    if (selectedBusinessEntityId) {
      const businessId = String(selectedBusinessEntityId);
      const merchantIdsForBusiness = merchantBusinessEntityMapping[businessId] || [];
      setMerchants(
        merchantsByCountry.filter((m) => merchantIdsForBusiness.includes(String(m.merchant_id)))
      );
    } else {
      setMerchants(merchantsByCountry);
    }
  }, [selectedOxygen7Entity, selectedBusinessEntityId, allBusinessEntities, allMerchants, merchantBusinessEntityMapping]);

  useEffect(() => {
    loadReports();
  }, []);

  const handleSearch = () => {
    setPage(0);
    loadReports();
  };

  const handleReset = () => {
    setSettlementDate(new Date());
    setSelectedMerchantId(null);
    setSelectedBusinessEntityId(null);
    setSelectedOxygen7Entity(availableOxygen7Entities[0]?.value || DEFAULT_OXYGEN7_ENTITY);
    setAllMerchants([]);
    setAllBusinessEntities([]);
    setMerchants([]);
    setBusinessEntities([]);
    setMerchantBusinessEntityMapping({});
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMerchantChange = (event) => {
    const value = event.target.value;
    // Convert 'all' to null, otherwise use the merchant_id
    setSelectedMerchantId(value === 'all' ? null : value);
  };

  const handleBusinessEntityChange = (event) => {
    setSelectedBusinessEntityId(event.target.value === 'all' ? null : event.target.value);
    setSelectedMerchantId(null);
  };

  const handleFileNameClick = (report) => {
    setSelectedReport(report);
    setOpenDownloadDialog(true);
  };

  const handleDownloadConfirm = async () => {
    if (!selectedReport) return;
    
    try {
      await downloadFile(selectedReport);
    } catch (error) {
      showErrorMsg('Failed to download file');
    }
    
    setOpenDownloadDialog(false);
    setSelectedReport(null);
  };

  const handleDownloadCancel = () => {
    setOpenDownloadDialog(false);
    setSelectedReport(null);
  };

  const downloadFile = async (report) => {
    try {
      
      if (!report.file_name) {
        showErrorMsg('Report name is missing');
        return;
      }
      
      const reportPath = report.report_path;
      
      if (!reportPath) {
        showErrorMsg('Report path is missing');
        return;
      }
      
      // Use the generic download_file endpoint
      const registeredCountry = report.registered_country || selectedOxygen7Entity;
      
      const response = await api.post('/payout/report/download_file/', {
        report_path: reportPath,
        file_name: report.file_name,
        registered_country: registeredCountry
      }, {
        responseType: 'blob'
      });

      if (response.status === 200) {
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = report.file_name;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        showSuccessMsg(`Download completed: ${report.file_name}`);
      } else {
        showErrorMsg(`Download failed: ${response.status} ${response.statusText}`);
      }
      
    } catch (error) {
        showErrorMsg(`Download failed: ${error.response?.data?.detail || error.message}`);
      throw error;
    }
  };

  const paginatedReports = reports.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <HeaderHelpPage title="Merchant Reports" isShowLink={false} />
      
      <Card sx={{ mb: 2, px: 2, py: 2, borderRadius: 2, border: '1px solid #f0f0f0' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <Box>
              <Typography sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }} variant="subtitle1">
                Oxygen7 Entity
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={selectedOxygen7Entity}
                  onChange={(e) => setSelectedOxygen7Entity(e.target.value)}
                  size="small"
                  displayEmpty
                  sx={{
                    height: "30px",
                    "& .MuiSelect-select": {
                      fontSize: "14px",
                    },
                  }}
                >
                  {availableOxygen7Entities.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Box>
              <Typography sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }} variant="subtitle1">
                Settlement Date
              </Typography>
              <CustomDatePicker
                value={settlementDate}
                onChange={(newValue) => setSettlementDate(newValue)}
                fullWidth
                size="small"
                views={["year", "month", "day"]}
                openTo="day"
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    height: "30px",
                    fontSize: "14px"
                  }
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Box>
              <Typography sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }} variant="subtitle1">
                Business Entity
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={selectedBusinessEntityId === null ? 'all' : selectedBusinessEntityId}
                  onChange={handleBusinessEntityChange}
                  size="small"
                  displayEmpty
                  sx={{
                    height: "30px",
                    "& .MuiSelect-select": {
                      fontSize: "14px",
                    },
                  }}
                >
                  <MenuItem value="all">All Business Entities</MenuItem>
                  {businessEntities.map((businessEntity) => (
                    <MenuItem key={businessEntity.business_entity_id} value={businessEntity.business_entity_id}>
                      {businessEntity.business_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Box>
              <Typography sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }} variant="subtitle1">
                Merchant
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={selectedMerchantId === null ? 'all' : selectedMerchantId}
                  onChange={handleMerchantChange}
                  size="small"
                  displayEmpty
                  sx={{
                    height: "30px",
                    "& .MuiSelect-select": {
                      fontSize: "14px",
                    },
                  }}
                >
                  <MenuItem value="all">All Merchants</MenuItem>
                  {merchants.map((merchant) => (
                    <MenuItem key={merchant.merchant_id} value={merchant.merchant_id}>
                      {merchant.merchant_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>

        </Grid>

        <Stack direction="row" justifyContent="flex-end">
          <ButtonCustom
            sx={{ ml: 1, mt: 2, ...styles.customButton }}
            icon={<Search />}
            variant="contained"
            title="Search"
            onClick={handleSearch}
          />
          <ButtonCustom
            sx={{ ml: 1, mt: 2 }}
            icon={<Refresh />}
            title="Reset"
            onClick={handleReset}
          />
        </Stack>
      </Card>

      {loading ? (
        <CircularLoader />
      ) : (
        <Card sx={{ borderRadius: 2, border: '1px solid #f0f0f0' }}>
          <TableContainer>
            <Table sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      minWidth: 400,
                      width: '70%'
                    }}
                  >
                    File Name
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      minWidth: 100
                    }}
                  >
                    Size
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedReports.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 3 }}>
                      <Typography
                        gutterBottom
                        align="center"
                        variant="subtitle1"
                      >
                        Not found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedReports.map((report) => (
                    <TableRow key={report.id}>
                      <TableCell>
                        <Tooltip title={report.file_name}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#1b6ac9',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              '&:hover': {
                                color: '#1976d2',
                                textDecoration: 'underline'
                              },
                              wordBreak: 'break-word',
                              lineHeight: 1.4
                            }}
                            onClick={() => handleFileNameClick(report)}
                          >
                            {report.file_name}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{report.file_size_formatted}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={reports.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      )}

      <Dialog
        open={openDownloadDialog}
        onClose={handleDownloadCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Download Confirmation
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Do you want to download{' '}
            <Typography 
              component="span" 
              sx={{ 
                fontWeight: 600, 
                color: '#1b6ac9',
                wordBreak: 'break-word',
                whiteSpace: 'normal'
              }}
            >
              {selectedReport?.file_name}
            </Typography>
            ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleDownloadCancel} 
            variant="outlined"
            sx={{ mr: 1 }}
          >
            No
          </Button>
          <Button 
            onClick={handleDownloadConfirm} 
            variant="contained"
            sx={{ 
              backgroundColor: COLORS.OXYGEN7_PURPLE,
              '&:hover': {
                backgroundColor: 'rgba(91, 45, 130, 0.8)'
              }
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MerchantReports;
